import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { Crosshair, Check } from "lucide-react";
import { toast } from "sonner";

// Soft per-member colours (amber is reserved for the idle alert).
const PALETTE = [
  { bg: "#ecfdf5", bar: "#34d399", chip: "#d1fae5", text: "#065f46" },  // mint
  { bg: "#f0f9ff", bar: "#38bdf8", chip: "#e0f2fe", text: "#075985" },  // sky
  { bg: "#f5f3ff", bar: "#a78bfa", chip: "#ede9fe", text: "#5b21b6" },  // lavender
  { bg: "#fdf2f8", bar: "#f472b6", chip: "#fce7f3", text: "#9d174d" },  // pink
  { bg: "#f0fdfa", bar: "#2dd4bf", chip: "#ccfbf1", text: "#115e59" },  // teal
  { bg: "#f7fee7", bar: "#a3e635", chip: "#ecfccb", text: "#3f6212" },  // lime
  { bg: "#eef2ff", bar: "#818cf8", chip: "#e0e7ff", text: "#3730a3" },  // indigo
];
const IDLE = { bg: "#fffbeb", bar: "#f59e0b", chip: "#fef3c7", text: "#92400e" };
export const IDLE_MIN = 30;

const hash = (s) => [...(s || "")].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7);

// Colour + idle state for a working-parcel entry {email, name, at}.
export const workLook = (w) => {
  if (!w) return null;
  const mins = w.at ? Math.floor((Date.now() - new Date(w.at).getTime()) / 60000) : 0;
  const idle = mins >= IDLE_MIN;
  const pick = Number.isInteger(w.colour) && w.colour >= 0 && w.colour < PALETTE.length
    ? w.colour : hash((w.email || "").toLowerCase()) % PALETTE.length;
  const c = idle ? IDLE : PALETTE[pick];
  const ago = mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
  return { ...c, idle, mins, ago, rowStyle: { backgroundColor: c.bg, boxShadow: `inset 4px 0 0 ${c.bar}` } };
};

export const WorkingTag = ({ w, testid }) => {
  const l = workLook(w);
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-px text-[9px] font-bold whitespace-nowrap"
      style={{ backgroundColor: l.chip, color: l.text }} data-testid={testid}
      title={`${w.name} — working on this parcel (invoice ${w.invoice_no}) · last move ${l.ago} ago${l.idle ? " — idle!" : ""}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${l.idle ? "" : "animate-pulse"}`} style={{ backgroundColor: l.bar }} />
      {w.name}{l.idle && ` · idle ${l.ago}`}
    </span>
  );
};

const NAMES = ["Mint", "Sky", "Lavender", "Pink", "Teal", "Lime", "Indigo"];

// Iter 316 — pick your own highlight colour (saved on your user; everyone sees it).
export const ColourDot = ({ user, working, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(null);
  const me = (user?.email || "").toLowerCase();
  const mine = working.find(w => (w.email || "").toLowerCase() === me);
  const cur = workLook({ ...(mine || {}), email: me, at: null, colour: picked ?? mine?.colour ?? user?.or_colour });
  const save = async (i) => {
    try {
      await axios.post(`${API}/order-register/working-colour`, { colour: i }, { headers: getAuthHeader() });
      toast.success(`Highlight colour set to ${NAMES[i]}`);
      setPicked(i); setOpen(false); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save colour"); }
  };
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen(o => !o)} data-testid="or-colour-dot" title="Choose your highlight colour"
        className="w-6 h-6 rounded-full border-2 shadow-sm" style={{ backgroundColor: cur.bg, borderColor: cur.bar }} />
      {open && (
        <div className="absolute left-0 top-full mt-1 z-40 flex gap-1.5 rounded-lg border border-slate-200 bg-white p-2 shadow-lg" data-testid="or-colour-picker">
          {PALETTE.map((p, i) => (
            <button key={i} type="button" onClick={() => save(i)} title={NAMES[i]} data-testid={`or-colour-${i}`}
              className="w-6 h-6 rounded-full border-2 flex items-center justify-center" style={{ backgroundColor: p.bg, borderColor: p.bar }}>
              {cur.bar === p.bar && <Check className="w-3 h-3" style={{ color: p.text }} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const findEl = (email) => [...document.querySelectorAll(`tr[data-working="${CSS.escape(email)}"], [data-card-working="${CSS.escape(email)}"]`)]
  .find(el => el.offsetParent !== null);
const flash = (el) => {
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  el.classList.add("or-working-flash");
  setTimeout(() => el.classList.remove("or-working-flash"), 1600);
};
const chan = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("or-working-jump") : null;

// Scroll to the signed-in user's highlighted parcel; clears filters + loads its invoice if hidden.
// Clicking in one browser tab makes every open Order Register tab jump too.
export const JumpToWorkingButton = ({ user, working, onFocus }) => {
  const me = (user?.email || "").toLowerCase();
  const mine = working.find(w => (w.email || "").toLowerCase() === me);
  const ref = useRef({});
  ref.current = { mine, onFocus };
  const jump = (quiet) => {
    const m = ref.current.mine;
    if (!m) return;
    const el = findEl(m.email);
    if (el) return flash(el);
    if (!quiet) toast.info(`Opening your working parcel (${m.customer_name || ""}${m.invoice_no ? ` · ${m.invoice_no}` : ""})`);
    ref.current.onFocus(m);
    let n = 0;
    const t = setInterval(() => {
      const e2 = findEl(m.email);
      if (e2) { clearInterval(t); flash(e2); } else if (++n > 40) { clearInterval(t); if (!quiet) toast.error("Couldn't find your working parcel in the table"); }
    }, 250);
  };
  useEffect(() => {
    if (!chan) return undefined;
    const h = (ev) => { if ((ev.data?.email || "").toLowerCase() === me) jump(true); };
    chan.addEventListener("message", h);
    return () => chan.removeEventListener("message", h);
  }, [me]);
  if (!mine) return null;
  const go = () => { jump(false); chan?.postMessage({ email: me }); };
  const l = workLook(mine);
  return (
    <button type="button" onClick={go} data-testid="or-jump-working"
      className="inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-semibold"
      style={{ backgroundColor: l.chip, color: l.text, borderColor: l.bar }}>
      <Crosshair className="w-3.5 h-3.5" /> Go to my working parcel
      {l.idle && <span className="text-[10px]">· idle {l.ago}</span>}
    </button>
  );
};
